/**
 * The two switches that decide how much of the permission system is switched on.
 *
 * Both live here rather than beside the code that reads them, because every
 * layer — the sidebar filter, the route guard, the per-action hook and the
 * denied-control wrapper — has to agree on the answer. Two modules each reading
 * `process.env` for themselves is how one of them ends up gating while another
 * does not.
 *
 * ⚠️ CRA inlines REACT_APP_* at BUILD time. Changing either of these needs a
 * REBUILD, not a restart, and the value is baked into the bundle that ships.
 *
 * ⚠️ Neither is a security control. Both decide what the UI DRAWS. The
 * authorization boundary is app-backend's own route authorization
 * (ENABLE_ROUTE_AUTHZ), which is enforced server-side and completely unaffected
 * by anything in this file — turning RBAC off here does not widen what the API
 * will actually do for that operator.
 */

/**
 * Master switch. `false` turns the whole permission system off and every
 * operator sees and can use everything, exactly as before any of this existed.
 *
 * ON unless explicitly disabled, so an environment that never sets it gets the
 * intended behaviour rather than silently running ungated. Set
 * `REACT_APP_ENABLE_RBAC=false` for an environment whose role grants are not
 * ready yet — it restores the old portal without needing a revert.
 *
 * Only the exact string 'false' disables it. An unset, empty, misspelled or
 * mis-cased value leaves RBAC ON: the failure mode of a typo should be "still
 * protected", not "silently wide open".
 */
export const RBAC_ENABLED = process.env.REACT_APP_ENABLE_RBAC !== 'false';

/**
 * How a control the role may not use is presented.
 *
 *   false (default)  render it DISABLED, with a tooltip saying which permission
 *                    is missing
 *   true             do not render it at all
 *
 * Disabled-with-a-reason is the default because it is the more informative of
 * the two: the operator can see the capability exists and knows exactly what to
 * ask an administrator for, which turns a support conversation into a
 * copy-paste. Hiding is the right choice when the mere existence of a control is
 * itself noise — or something you would rather a role not know about — so it is
 * offered as a deployment decision rather than being argued in code.
 *
 * Set `REACT_APP_HIDE_DENIED_ACTIONS=true` to hide. Same strict-string rule as
 * above, in the same direction: only an explicit 'true' changes the behaviour.
 *
 * This governs ACTIONS ONLY — buttons, menu items, row icons. Whole SCREENS are
 * always hidden from the sidebar when denied and always answer a typed URL with
 * the 403 page, in both modes; a nav item that renders greyed-out is a worse
 * experience than one that is simply not there, and the 403 page is what makes
 * a stale bookmark explicable.
 */
export const HIDE_DENIED_ACTIONS = process.env.REACT_APP_HIDE_DENIED_ACTIONS === 'true';
